import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { commonStyles } from '../styles/common-styles';

@customElement('dance-figures-site')
export class DanceFiguresSite extends LitElement {
  private mobileQuery = window.matchMedia('(max-width: 480px)');
  private mediumQuery = window.matchMedia('(max-width: 1024px)');

  @state()
  private isMobile = this.mobileQuery.matches;

  @state()
  private isMedium = this.mediumQuery.matches && !this.mobileQuery.matches;

  override connectedCallback() {
    super.connectedCallback();
    // Listen for the specific moment the query crosses the threshold
    this.mobileQuery.addEventListener('change', this.handleQueryChange);
    this.mediumQuery.addEventListener('change', this.handleQueryChange);
  }

  private handleQueryChange = () => {
    this.isMobile = this.mobileQuery.matches;
    this.isMedium = this.mediumQuery.matches && !this.mobileQuery.matches;
  };

  override disconnectedCallback() {
    this.mobileQuery.removeEventListener('change', this.handleQueryChange);
    this.mediumQuery.removeEventListener('change', this.handleQueryChange);
    super.disconnectedCallback();
  }

  static override styles = [
    commonStyles,
    css`
      :host {
        display: block;
        --accent-colour: #6e90c0;
        --held-by-the-sea-bg: #b88393;
        background-color: var(--held-by-the-sea-bg);
      }
    `,
  ];
  override render() {
    const config = {
      heightVh: this.isMobile ? 80 : 220,
      panelHeight: this.isMobile ? 150 : 400,
      imgUrl: this.isMobile ? '/img/mobile/' : this.isMedium ? '/img/medium/' : '/img/',
    };
    const layersContent = {
      heldByTheWind: {
        altPrefix: 'Dance figures art collection — ',
        backgroundAltSuffix: 'Background layer evoking abstract wind movement shapes',
        figCaption:
          'Dance figures painting: Held by the Wind, three female figures in elegant poses with flowing air marks, evoking a sense of movement and freedom.',
        paintingName: 'Held by the Wind',
      },
      movedByTheTide: {
        altPrefix: 'Dance figures art collection — ',
        backgroundAltSuffix: 'Abstract layer with brushwork evoking the powerful movement of sea currents',
        figCaption:
          'Dance figures in motion: Moved by the Tide, three male figures in powerful poses with brush marks representing the tide as they move through the scene.',
        paintingName: 'Moved by the Tide',
      },
      songOfTheSwayingDunesI: {
        altPrefix: 'Dance figurative art collection — ',
        backgroundAltSuffix: 'Abstract layer of sand-hued tones supporting the swaying figures',
        figCaption: "Painting: The Song of the Swaying Dunes I. The female figures, arms open, caught in a moment of weightless suspension, part one of a diptych",
        paintingName: 'Song of the Swaying Dunes I',
      },
      songOfTheSwayingDunesII: {
        altPrefix: 'Dance figurative art collection — ',
        backgroundAltSuffix: 'Gestural abstract marks evoking drifting sand and open space',
        figCaption: "Painting: The Song of the Swaying Dunes II. The female figures continue their movement, a celebration of the grace of the body. part two of a diptych.",
        paintingName: 'Song of the Swaying Dunes II',
      },
    };

    return html`
      <dance-figures-layout>
        <site-navigation slot="navigation"></site-navigation>
        <site-header-panel slot="site-header"></site-header-panel>

        <!-- negative startPos is up -->
        <!-- negative pos x is left -->
        <panel-layout slot="artwork-held-by-the-wind">
          <hero-parallax-panel
            slot="hero"
            style="--panel-background-image: url('${config.imgUrl}held-background-coloured-big.avif'); --panel-height: ${this.isMobile
        ? 130
        : 420}vh; --panel-background-position: center 0;"
            .caption=${layersContent.heldByTheWind.figCaption}
            .sectionID=${'held-by-the-wind'}
            .layers=${[
        {
          // front
          alt: `${layersContent.heldByTheWind.altPrefix}${layersContent.heldByTheWind.paintingName}. ${layersContent.heldByTheWind.backgroundAltSuffix}`,
          src: `${config.imgUrl}held-wind-layer-3b.avif`,
          speed: 0.1,
          topPct: 0.5,
          heightVh: config.heightVh,
          scale: 0.8,
          leftPct: 9,
          ...(this.isMobile && {
            scale: 0.8,
            leftPct: 9,
          }),
          ...(this.isMedium && {
            scale: 0.6,
            leftPct: 22,
          }),
          // leftPct: 9,
          cssName: 'layer-1',
          fetchPriority: 'high',
        },
        {
          // front
          alt: `${layersContent.heldByTheWind.paintingName}. Foreground layer showing a figure with arched back, emerging, dynamic movement, dance figures art.`,
          src: `${config.imgUrl}held-wind-layer-3a.avif`,
          speed: 0.12,
          topPct: 0,
          heightVh: config.heightVh,
          scale: 0.8,
          leftPct: 9,
          ...(this.isMedium
            ? {
              scale: 0.6,
              leftPct: 22,
            }
            : {
              scale: 0.8,
              leftPct: 9,
            }),
          cssName: 'layer-1',
          fetchPriority: 'high',
        },
        {
          // middle
          alt: `${layersContent.heldByTheWind.paintingName}. Foreground layer showing a figure in acrobatic movement, dance figures art series.`,
          src: `${config.imgUrl}held-wind-layer-2a.avif`,
          speed: 0.23,
          topPct: 25,
          scale: 1.1,
          ...(!this.isMobile &&
            !this.isMedium && {
            leftPct: -22,

            // edgeAnchor: { edge: 'left', offset: '1px' },
          }),
          ...(this.isMobile && {
            topPct: 27,
            scale: 1,
            leftPct: -15,
          }),
          ...(this.isMedium && {
            scale: 0.8,
            leftPct: -22,

            // edgeAnchor: { edge: 'left', offset: '-500px' },
          }),
          heightVh: config.heightVh,
          cssName: 'layer-2',
          fetchPriority: 'high',
        },
        {
          // middle
          alt: `${layersContent.heldByTheWind.altPrefix}${layersContent.heldByTheWind.paintingName}. ${layersContent.heldByTheWind.backgroundAltSuffix}`,
          src: `${config.imgUrl}held-wind-layer-2b.avif`,
          speed: 0.17,
          topPct: 28,
          scale: 1,
          ...(!this.isMobile &&
            !this.isMedium && {
            leftPct: -12,
            // edgeAnchor: { edge: 'left', offset: '-600px' },
          }),
          ...(this.isMobile && {
            topPct: 27,
            scale: 0.8,
            leftPct: -1,
            // edgeAnchor: { edge: 'left', offset: '-38%' },
          }),
          ...(this.isMedium && {
            scale: 0.9,
            // edgeAnchor: { edge: 'left', offset: '-300px' },
            leftPct: -4,
          }),
          heightVh: config.heightVh,
          cssName: 'layer-2',
          fetchPriority: 'high',
        },
        {
          // last
          alt: `${layersContent.heldByTheWind.altPrefix}${layersContent.heldByTheWind.paintingName}. ${layersContent.heldByTheWind.backgroundAltSuffix}`,
          src: `${config.imgUrl}held-wind-layer-1bb.avif`,
          speed: 0.13,
          topPct: 22,
          leftPct: 13,
          scale: this.isMedium ? 0.6 : 0.8,
          heightVh: config.heightVh,
          cssName: 'layer-3',
          fetchPriority: 'high',
        },
        {
          // last
          alt: `Foreground layer showing the main female figure in serene joyful pose with face visible as she is experiencing being ${layersContent.heldByTheWind.paintingName}.`,
          src: `${config.imgUrl}held-wind-layer-1aaa.avif`,
          speed: 0.17,
          topPct: 21,
          leftPct: 13,
          scale: this.isMedium ? 0.6 : 0.8,
          heightVh: config.heightVh,
          cssName: 'layer-3',
          fetchPriority: 'high',
        },
      ]}
          >
          </hero-parallax-panel>

          <artwork-in-scene
            slot="artwork-in-scene"
            plaqueText="Held by the Wind"
            stageImage="${config.imgUrl}interior-held-wind.avif"
            .layers=${[
        {
          src: `${config.imgUrl}held-by-the-wind-painting.avif`,
          speed: 0,
          objectFit: 'contain',
          startPos: '-320',
          cssName: 'background',
          alt: 'Painting: Held by the Wind',
        },
      ]}
          >
          </artwork-in-scene>
        </panel-layout>

        <panel-layout slot="artwork-moved-by-the-tide">
          <hero-parallax-panel
            slot="hero"
            style="--panel-background-image: url('${config.imgUrl}moved-by-tide-bg1.avif'); --panel-height: ${this.isMobile ? 120 : 352}vh;"
            .words=${['Moved', 'by', 'the', 'Tide']}
            .sectionID=${'moved-by-the-tide'}
            .caption=${layersContent.movedByTheTide.figCaption}
            .layers=${[
        {
          alt: `${layersContent.movedByTheTide.altPrefix}${layersContent.movedByTheTide.paintingName}. ${layersContent.movedByTheTide.backgroundAltSuffix}`,
          speed: 0.12,
          src: `${config.imgUrl}moved-by-tide-layer-3b.avif`,
          leftPct: 20,
          topPct: -1.5,
          heightVh: config.heightVh,
          scale: this.isMedium ? 0.6 : 0.8,
          cssName: 'layer-3',
        },
        {
          alt: `Male figure reaching upward in a powerful gesture, from the ${layersContent.movedByTheTide.paintingName} series.`,
          speed: 0.15,
          src: `${config.imgUrl}moved-by-tide-layer-3a.avif`,
          leftPct: 20,
          topPct: -2,
          heightVh: config.heightVh,
          scale: this.isMedium ? 0.6 : 0.8,
          cssName: 'layer-3',
        },
        {
          alt: `Translucent swirls of paint suggesting tidal forces — ${layersContent.movedByTheTide.paintingName}.`,
          src: `${config.imgUrl}moved-by-tide-layer-2b.avif`,
          speed: 0.14,
          topPct: 18,
          heightVh: config.heightVh,
          leftPct: 0,
          cssName: 'layer-2',
        },
        {
          alt: `Central male figure twisting his torso as he strides through the waves with face visible as he is ${layersContent.movedByTheTide.paintingName}.`,
          src: `${config.imgUrl}moved-by-tide-layer-2a.avif`,
          speed: 0.18,
          topPct: 17,
          heightVh: config.heightVh,
          leftPct: 0,
          cssName: 'layer-2',
        },
        {
          alt: `Abstract brush strokes conveying the weight of a retreating tide — ${layersContent.movedByTheTide.paintingName}.`,
          speed: 0.1,
          src: `${config.imgUrl}moved-by-tide-layer-1b.avif`,
          topPct: 24.5,
          heightVh: config.heightVh,
          cssName: 'layer-1',
        },
        {
          alt: `Grounded male figure crouching in a resolute stance, with face visible - ${layersContent.movedByTheTide.paintingName}.`,
          speed: 0.14,
          src: `${config.imgUrl}moved-by-tide-layer-1a.avif`,
          topPct: 24,
          heightVh: config.heightVh,
          cssName: 'layer-1',
        },
      ]}
          >
          </hero-parallax-panel>
          <artwork-in-scene
            slot="artwork-in-scene"
            plaqueText="Moved by the Tide"
            style="--panel-bg-gradient: linear-gradient(305deg,rgba(173, 176, 169, 1) 0%, rgba(194, 182, 194, 1) 51%, rgba(227, 227, 227, 1) 100%)"
            stageImage="${config.imgUrl}interior-moved-by-tide.avif"
            .layers=${[
        {
          src: `${config.imgUrl}moved-by-tide-painting.avif`,
          speed: 0,
          objectFit: 'contain',
          startPos: '-320',
          cssName: 'background',
          alt: 'Painting: Moved by the Tide',
        },
      ]}
          >
          </artwork-in-scene>
        </panel-layout>

        <panel-layout slot="artwork-dunes-part-1">
          <hero-parallax-panel
            slot="hero"
            style="--panel-background-image: url('${config.imgUrl}dunes-2-background.avif'); --panel-height: ${this.isMobile ? 123 : 360}vh;"
            .words=${['Dunes', 'I']}
            .caption=${layersContent.songOfTheSwayingDunesI.figCaption}
            .sectionID=${'song-of-the-swaying-dunes-i'}
            .layers=${[
        {
          alt: `${layersContent.songOfTheSwayingDunesI.altPrefix}${layersContent.songOfTheSwayingDunesI.backgroundAltSuffix}`,
          speed: 0.14,
          src: `${config.imgUrl}dunes-1-layer-3b.avif`,
          topPct: 12,
          leftPct: -2,
          heightVh: config.heightVh,
          scale: this.isMedium ? 0.6 : 0.9,
          cssName: 'layer-3',
        },
        {
          alt: `A female figure facing left with arms outstretched behind, caught in a moment of weightless suspension — ${layersContent.songOfTheSwayingDunesI.paintingName}.`,
          speed: 0.18,
          src: `${config.imgUrl}dunes-1-layer-3a.avif`,
          topPct: 10,
          leftPct: -2,
          heightVh: config.heightVh,
          scale: this.isMedium ? 0.6 : 0.9,
          cssName: 'layer-3',
        },
        {
          alt: `${layersContent.songOfTheSwayingDunesI.altPrefix}${layersContent.songOfTheSwayingDunesI.backgroundAltSuffix}`,
          src: `${config.imgUrl}dunes-1-layer-2b.avif`,
          speed: 0.16,
          topPct: 1,
          heightVh: config.heightVh,
          scale: this.isMedium ? 0.6 : 1.05,
          leftPct: 15,
          cssName: 'layer-2',
        },
        {
          alt: `Central female form beautifully rendered portrait visible as she smiles serenely, from the ${layersContent.songOfTheSwayingDunesI.paintingName} diptych.`,
          src: `${config.imgUrl}dunes-1-layer-2a.avif`,
          speed: 0.2,
          topPct: 0,
          heightVh: config.heightVh,
          scale: this.isMedium ? 0.6 : 1.05,
          leftPct: 15,
          cssName: 'layer-2',
        },

        {
          alt: `Abstract textures with layered paint`,
          speed: 0.1,
          src: `${config.imgUrl}dunes-1-layer-1b.avif`,
          topPct: 25,
          edgeAnchor: { edge: 'left', offset: 0 },
          heightVh: config.heightVh,
          scale: this.isMedium ? 0.6 : 1.15,
          cssName: 'layer-1',
        },
        {
          alt: `Caught mid movement, a female figure hugging her legs, from the ${layersContent.songOfTheSwayingDunesI.paintingName} diptych.`,
          speed: 0.14,
          src: `${config.imgUrl}dunes-1-layer-1a.avif`,
          topPct: 25,
          edgeAnchor: { edge: 'left', offset: 0 },
          heightVh: config.heightVh,
          scale: this.isMedium ? 0.6 : 1.15,
          cssName: 'layer-1',
        },
      ]}
          >
          </hero-parallax-panel>
          <artwork-in-scene
            slot="artwork-in-scene"
            plaqueText="The Song of the Swaying Dunes I"
            style="--panel-bg-gradient: linear-gradient(305deg, rgb(142 134 178) 0%, rgb(199 177 164) 59% 65%, rgb(236 203 204) 100%)"
            stageImage="${config.imgUrl}interior-dunes-1.avif"
            stageImageEndTranslateYPos="15"
            .layers=${[
        {
          src: `${config.imgUrl}dunes-1-painting.avif`,
          speed: 0,
          objectFit: 'contain',
          startPos: '-320',
          cssName: 'background',
          alt: 'Painting: The Song of the Swaying Dunes I',
        },
      ]}
          >
          </artwork-in-scene>
        </panel-layout>

        <panel-layout slot="artwork-dunes-part-2">
          <hero-parallax-panel
            slot="hero"
            style="--panel-background-image: url('${config.imgUrl}dunes-2-background.avif'); --panel-height: ${config.panelHeight}vh;"
            .words=${['Dunes', 'II']}
            .caption=${layersContent.songOfTheSwayingDunesII.figCaption}
            .sectionID=${'song-of-the-swaying-dunes-ii'}
            .layers=${[
        {
          alt: `Upper figure in an extended pose, immersed in the ${layersContent.songOfTheSwayingDunesII.paintingName} composition.`,
          speed: 0.2,
          src: `${config.imgUrl}dunes-2-layer-4a.avif`,
          topPct: 22,
          heightVh: config.heightVh,
          scale: this.isMedium ? 0.6 : 0.9,
          // rightPct: 6,
          cssName: 'layer-4',
          edgeAnchor: { edge: 'left', offset: '5px' },
        },
        {
          alt: `${layersContent.songOfTheSwayingDunesII.altPrefix}${layersContent.songOfTheSwayingDunesII.backgroundAltSuffix}`,
          speed: 0.14,
          src: `${config.imgUrl}dunes-2-layer-4b.avif`,
          topPct: 22,
          heightVh: config.heightVh,
          scale: this.isMedium ? 0.6 : 0.9,
          rightPct: 6,
          edgeAnchor: { edge: 'left', offset: '5px' },
          cssName: 'layer-4',
        },

        {
          alt: `Background of colourful brush strokes.`,
          speed: 0.15,
          src: `${config.imgUrl}dunes-2-layer-3b.avif`,
          topPct: 25,
          leftPct: 8,
          heightVh: config.heightVh,
          scale: this.isMedium ? 0.6 : 0.9,
          cssName: 'layer-3',
        },
        {
          alt: `Background female figure with arched back suspended in the mid of the dance, arms tracing the air.`,
          speed: 0.2,
          src: `${config.imgUrl}dunes-2-layer-3a.avif`,
          topPct: 25,
          leftPct: 8,
          heightVh: config.heightVh,
          scale: this.isMedium ? 0.6 : 0.9,
          cssName: 'layer-3',
        },

        {
          alt: `${layersContent.songOfTheSwayingDunesII.altPrefix}${layersContent.songOfTheSwayingDunesII.backgroundAltSuffix}`,
          src: `${config.imgUrl}dunes-2-layer-2b.avif`,
          speed: 0.17,
          topPct: 1,
          heightVh: config.heightVh,
          scale: this.isMedium ? 0.6 : 0.8,
          leftPct: 23,
          cssName: 'layer-2',
        },
        {
          alt: `Large powerful female figure in a dynamic jump action, from the ${layersContent.songOfTheSwayingDunesII.paintingName} diptych.`,
          src: `${config.imgUrl}dunes-2-layer-2a.avif`,
          speed: 0.2,
          topPct: 0,
          heightVh: config.heightVh,
          scale: this.isMedium ? 0.6 : 0.8,
          leftPct: 15,
          cssName: 'layer-2',
        },
        {
          alt: `Frontmost figure in close detail, expressive face and gesture as she leans in towards the viewer — ${layersContent.songOfTheSwayingDunesII.paintingName}.`,
          speed: 0.07,
          src: `${config.imgUrl}dunes-2-layer-1aa.avif`,
          topPct: 40,
          leftPct: -4,
          // leftPct: 24,
          heightVh: config.heightVh,
          scale: this.isMedium ? 0.6 : 1,
          cssName: 'layer-1',
        },
        {
          alt: `Abstract textures and brush marks to support the figures`,
          speed: 0.02,
          src: `${config.imgUrl}dunes-2-layer-1b.avif`,
          topPct: 47,
          leftPct: -1,
          heightVh: config.heightVh,
          scale: this.isMedium ? 0.6 : 1,
          cssName: 'layer-1',
        },
      ]}
          >
          </hero-parallax-panel>
          <artwork-in-scene
            slot="artwork-in-scene"
            plaqueText="Song of the Swaying Dunes II"
            style="--frame-width: 70vh;"
            stageImage="${config.imgUrl}interior-dunes-2.avif"
            stageImageEndTranslateYPos="15"
            .layers=${[
        {
          src: `${config.imgUrl}dunes-2-painting.avif`,
          speed: 0,
          objectFit: 'contain',
          startPos: '-320',
          cssName: 'background',
          alt: 'Painting: Song of the Swaying Dunes II',
        },
      ]}
          >
          </artwork-in-scene>
        </panel-layout>
      </dance-figures-layout>
    `;
  }
}
